import {dataTestId} from '@hconnect/uikit'
import {CardTitle} from '@hconnect/uikit/src/lib2'
import {Dialog, DialogContent, DialogActions, Stack, Typography} from '@mui/material'
import React, {useState, useContext, useMemo, useCallback} from 'react'

/**
 * Id for the default header's title element so the Dialog's `aria-labelledby`
 * resolves to a real node, giving screen readers an accessible name.
 * Custom headers (which the provider cannot introspect) instead supply an
 * `ariaLabel` via `openDialog` so the dialog still has an accessible name.
 */
export const DIALOG_TITLE_ID = 'dialog-title'

interface DefaultDialogHeader {
  title: string
  subtitle?: string
}

const isDefaultDialogHeader = (
  dialogHeader: React.ReactNode | DefaultDialogHeader
): dialogHeader is DefaultDialogHeader => {
  return dialogHeader !== null && typeof dialogHeader === 'object' && 'title' in dialogHeader
}

interface DialogState {
  header: React.ReactNode | DefaultDialogHeader
  content: React.ReactNode
  actions?: React.ReactNode
  dataTestId?: string
  /**
   * Accessible name for the dialog when a custom `header` is used. A custom
   * header has no title element the provider can reference via `aria-labelledby`,
   * so callers pass the name here to keep the dialog labelled for screen readers.
   */
  ariaLabel?: string
}

interface DialogProviderState {
  openDialog: (details: DialogState) => void
  closeDialog: () => void
}
const DialogContext = React.createContext<DialogProviderState | undefined>(undefined)

export const useDialog = (): DialogProviderState => {
  const context = useContext(DialogContext)
  if (!context)
    throw new Error(
      'Cannot use the dialog without having DialogProvider upper in the component tree'
    )
  return context
}

/**
 * This provider is intended to have a simple interface for having a pop up dialog that forces the user to confirm an action.
 */
export const DialogProvider = ({children}: {children: React.ReactNode}) => {
  const [dialogState, setDialogState] = useState<DialogState | null>(null)

  const closeDialog = useCallback(() => setDialogState(null), [])

  const contextValue = useMemo<DialogProviderState>(
    () => ({openDialog: setDialogState, closeDialog}),
    [closeDialog]
  )

  return (
    <DialogContext.Provider value={contextValue}>
      <Dialog
        open={!!dialogState}
        onClose={closeDialog}
        maxWidth={false}
        aria-labelledby={isDefaultDialogHeader(dialogState?.header) ? DIALOG_TITLE_ID : undefined}
        // aria-label must sit on the role="dialog" Paper (a bare Dialog prop lands
        // on the outer Modal root instead), so route it through PaperProps.
        PaperProps={{'aria-label': dialogState?.ariaLabel}}
        scroll="paper"
        {...dataTestId(dialogState?.dataTestId ?? 'dialog')}
      >
        {dialogState && (
          <Stack spacing={2} sx={{p: 3}}>
            {isDefaultDialogHeader(dialogState.header) ? (
              <Stack sx={{p: 0}}>
                <CardTitle
                  {...dataTestId('confirm_dialog_title')}
                  id={DIALOG_TITLE_ID}
                  variant="h3"
                  mb={0}
                >
                  {dialogState.header.title}
                </CardTitle>
                {dialogState.header.subtitle && (
                  <Typography variant="caption">{dialogState.header.subtitle}</Typography>
                )}
              </Stack>
            ) : (
              dialogState.header
            )}
            <DialogContent sx={{p: 0}}>{dialogState.content}</DialogContent>
            {dialogState.actions && (
              <DialogActions sx={{p: 0}}>{dialogState.actions}</DialogActions>
            )}
          </Stack>
        )}
      </Dialog>
      {children}
    </DialogContext.Provider>
  )
}
